import {
  getCreatorPageComponentPath,
  getPublicUrlWithUsername,
} from "../CustomDomain/CustomDomainFuntions";
import { CREATOR_PAGES_FROM } from "../componentsTable";
import {
  buildCreatorCollectionUrl,
  isViewingCreatorOnPlatformPath,
  keepNavigationOnVercelDeploy,
  type CreatorBrowsableUrlPrefs,
} from "./getCreatorPublicBaseUrl";
import { getCreatorEmbedPublicUrl } from "./getCreatorEmbedPublicUrl";
import { getCreatorSlugFromHost } from "./getCreatorSlugFromHost";
import { isVercelDeploymentHost } from "./creatorHost";
import {
  creatorPublicPathOmitsUsernamePrefix,
  getCreatorSpaRelativePath,
} from "./getCreatorSpaRelativePath";
import { isPublicWebsiteTemplateRoute, isInsideWebsiteTemplatePage } from "../websiteTemplateGuards";
import { hasPublishedWebsiteSurface } from "../SupabaseServices/ProductCollectionDatabase";
import { websiteCatalogPageForLibComponent, isCarouselSeriesTab } from "../StayUpToDate/stayUpToDateComponentTabFilters";
import { getSelectedCreatorPublicBaseUrl } from "./getSelectedCreatorPublicUrl";

export function isDashboardPathname(pathname?: string): boolean {
  return String(pathname || "").includes("/dashboard");
}

/** Infer embed / multipage / link-in-bio context while editing in the dashboard. */
export function resolveDashboardCollectionFrom(pathname?: string): string | null {
  const path = String(pathname || "");
  if (!isDashboardPathname(path)) return null;
  if (path.includes("/dashboard/embed-page")) return "embed-search";
  if (path.includes("/dashboard/creator-pages")) return CREATOR_PAGES_FROM;
  return "landing-page";
}

export function resolveDashboardCreatorPageSlug(
  pathname?: string,
): string | null {
  const match = String(pathname || "").match(
    /\/dashboard\/creator-pages\/([^/]+)/,
  );
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Prefer the host the visitor is already on (subdomain, custom domain, path-style). */
export function shouldPreserveViewerOriginForCollection(args: {
  username: string;
  pathname?: string;
}): boolean {
  if (typeof window === "undefined") return false;
  // Vercel deploys must never jump to browsable.ai / browsable.bio.
  if (isVercelDeploymentHost()) return true;
  const pathname = args.pathname ?? window.location.pathname;
  if (isDashboardPathname(pathname)) return false;

  // `/website/:user/...` is a public creator surface. Do not fall through to
  // browsable.ai/{username}/{slug} — clicks must stay on the website route.
  if (isPublicWebsiteTemplateRoute(pathname)) return true;

  if (creatorPublicPathOmitsUsernamePrefix()) return true;
  if (getCreatorSlugFromHost()) return true;
  return isViewingCreatorOnPlatformPath(args.username, pathname);
}

/** `shop/:id` or `content/:slug` — website Products / Content collection views. */
export function websiteCatalogRouteForLibComponent(args: {
  component?: any;
  componentSlug?: string;
}): string | null {
  const catalogPage = websiteCatalogPageForLibComponent(args.component);
  if (!catalogPage) return null;
  const slug = String(
    args.componentSlug ||
      args.component?.route_name ||
      args.component?.unique_id ||
      "",
  )
    .trim()
    .replace(/^\/+/, "");
  if (!slug) return null;
  // Internal overlay path. Public clicks use the last segment only (`/{user}/{id}`).
  if (catalogPage === "shop") return `shop/${encodeURIComponent(slug)}`;
  return isCarouselSeriesTab({ componentType: args.component?.type })
    ? `content/series/${encodeURIComponent(slug)}`
    : `content/${encodeURIComponent(slug)}`;
}

/** `/{user}/{id}` for clicks/share. Overlay still uses `shop|content/{id}`. */
function websiteCatalogPublicPathRest(args: {
  component?: any;
  componentSlug?: string;
}): string | null {
  const route = websiteCatalogRouteForLibComponent(args);
  if (!route) return null;
  return route.split("/").filter(Boolean).pop() || null;
}

/** Dashboard copy/share: absolute public catalog URL (ignores `/dashboard` path). */
export function getWebsiteCatalogPublicUrlForComponent(args: {
  username: string;
  component?: any;
  componentSlug?: string;
  userData?: any;
  customDomain?: string | null;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null;
}): string {
  const username = String(args.username || "").trim();
  if (!username) return "";
  if (
    !hasPublishedWebsiteSurface({
      username,
      userData: args.userData,
    })
  ) {
    return "";
  }
  const route = websiteCatalogPublicPathRest(args);
  if (!route) return "";
  const base = getSelectedCreatorPublicBaseUrl({
    username,
    customDomain: args.customDomain,
    browsableUrlPrefs: args.browsableUrlPrefs,
  }).replace(/\/+$/, "");
  return base ? `${base}/${route}` : "";
}

/** Canonical website Products/Content collection view for a LiB carousel. */
export function getWebsiteCatalogSpaPathForComponent(args: {
  username: string;
  component?: any;
  userData?: any;
  pathname?: string;
  from?: string | null;
  componentSlug?: string;
}): string | null {
  const username = String(args.username || "").trim();
  const pathname =
    args.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const from = String(args.from || "").trim();
  if (!username || from === CREATOR_PAGES_FROM) {
    return null;
  }
  // Dashboard LiB (not the website editor) keeps classic `/{user}/{slug}` URLs.
  const inWebsiteEditor = isInsideWebsiteTemplatePage();
  if (isDashboardPathname(pathname) && !inWebsiteEditor) {
    return null;
  }
  const route = websiteCatalogPublicPathRest(args);
  if (!route) return null;
  const useWebsiteCatalog =
    inWebsiteEditor ||
    isPublicWebsiteTemplateRoute(pathname) ||
    hasPublishedWebsiteSurface({
      username,
      userData: args.userData,
    });
  if (!useWebsiteCatalog) return null;
  return keepNavigationOnVercelDeploy(
    getCreatorSpaRelativePath({
      username,
      route,
      pathname: `/${username}`,
      isEmbedComponent: false,
    }),
  );
}

/** Public destination for a carousel "View All" click (relative or absolute). */
export function buildCollectionViewAllDestination(args: {
  username: string;
  componentSlug: string;
  from?: string | null;
  location?: { pathname?: string; search?: string };
  creatorPageSlug?: string | null;
  customDomain?: string | null;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null;
  isEmbedComponent?:boolean
  component?: any;
  userData?: any;
}): string {
  const username = String(args.username || "").trim();
  const componentSlug = String(args.componentSlug || "")
    .trim()
    .replace(/^\/+/, "");
  const from = String(args.from || "").trim();
  const pathname =
    args.location?.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");

  if (!username || !componentSlug) return "";

  const urlOpts = {
    username,
    customDomain: args.customDomain,
    browsableUrlPrefs: args.browsableUrlPrefs,
    isEmbedComponent:args.isEmbedComponent
  };

  const websiteCatalogPath = getWebsiteCatalogSpaPathForComponent({
    username,
    component: args.component,
    userData: args.userData,
    pathname,
    from,
    componentSlug,
  });
  if (websiteCatalogPath) return websiteCatalogPath;

  // Public website template: always SPA-relative (`/website/:user/link-in-bio/:slug`
  // on LiB, `/website/:user/:slug` elsewhere). Never emit browsable.ai absolute URLs.
  if (isPublicWebsiteTemplateRoute(pathname)) {
    return keepNavigationOnVercelDeploy(
      getCreatorSpaRelativePath({
        username,
        route: componentSlug,
        pathname,
        isEmbedComponent: args.isEmbedComponent,
      }),
    );
  }

  let destination = "";

  if (shouldPreserveViewerOriginForCollection({ username, pathname })) {
    if (from === CREATOR_PAGES_FROM) {
      const creatorPagePath = getCreatorPageComponentPath(
        componentSlug,
        args.location,
        args.creatorPageSlug,
        username,
      );
      if (creatorPagePath) destination = creatorPagePath;
    }

    if (!destination) {
      const creatorPageComponentPath = getCreatorPageComponentPath(
        componentSlug,
        args.location,
        args.creatorPageSlug,
        username,
      );
      if (creatorPageComponentPath) {
        destination = creatorPageComponentPath;
      } else {
        destination = getCreatorSpaRelativePath({
          username,
          route: componentSlug,
          pathname,
          isEmbedComponent:args.isEmbedComponent,
        });
      }
    }
  } else if (from === CREATOR_PAGES_FROM) {
    const creatorPagePath = getCreatorPageComponentPath(
      componentSlug,
      args.location,
      args.creatorPageSlug,
      username,
    );
    if (creatorPagePath) destination = creatorPagePath;
  }

  if (!destination && from === "embed-search") {
    const embedBase = getCreatorEmbedPublicUrl(urlOpts);
    if (embedBase) {
      destination = `${embedBase.replace(/\/+$/, "")}/${encodeURIComponent(componentSlug)}`;
    }
  }

  if (!destination) {
    const creatorPageComponentPath = getCreatorPageComponentPath(
      componentSlug,
      args.location,
      args.creatorPageSlug,
      username,
    );
    if (creatorPageComponentPath) {
      destination = creatorPageComponentPath;
    }
  }

  if (!destination) {
    const collectionUrl = buildCreatorCollectionUrl({
      ...urlOpts,
      route: componentSlug,
    });
    if (collectionUrl) destination = collectionUrl;
  }

  if (!destination) {
    destination = getPublicUrlWithUsername(username, componentSlug);
  }

  return keepNavigationOnVercelDeploy(destination);
}

export function toAbsolutePublicUrl(path: string, from?: string, isEmbedComponent?:boolean): string {
  const trimmed = keepNavigationOnVercelDeploy(String(path || "").trim());
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (typeof window === "undefined") return trimmed;

  const origin = window.location.origin;

  // When `from` is an embed context, we need /embed in the base — but only if:
  //   1. the origin doesn't already contain it (avoid double-prefixing on embed subdomains), AND
  //   2. the path itself doesn't already start with /embed (getCreatorSpaRelativePath already
  //      includes the /embed/ prefix when the current page is the embed view, so we must not
  //      add it again — that would produce /embed/embed/... URLs).
  // When `from` is NOT embed (e.g. "landing-page"), never add /embed.
  const pathAlreadyHasEmbedPrefix =
    trimmed === "/embed" || trimmed.startsWith("/embed/");
  const isEmbedContext =
    from?.includes("embed") && !isEmbedComponent
      ? !origin.includes("embed") && !pathAlreadyHasEmbedPrefix
      : false;

  const base = isEmbedContext
    ? `${origin}/embed`
    : `${origin}`;

  return trimmed.startsWith("/")
    ? `${base}${trimmed}`
    : `${base}/${trimmed}`;
}
